import React from 'react';
import { useLocale } from 'next-intl';
import { Box, Stack, Typography, Collapse } from '@unity/cloud-ui';
import { RichTreeView, TreeItem2, useTreeViewApiRef, useTreeItem2Utils, TreeItemProps } from '@unity/cloud-ui/TreeView';
import { useRouter } from 'next/navigation';
import type { SidebarTreeItem, SidebarSection, SearchResult } from '../types';

// recursively filter out only the items that are in the search results
const filterItems = (items: SidebarTreeItem[], searchResults: SearchResult[]) => {
  return items.reduce((acc, item) => {
    const children = filterItems(item.children, searchResults);
    const match = searchResults.find(({ value }) => value.includes(item.label));
    const isInSearch = match || children.length > 0;
    if (isInSearch) {
      if (match) {
        item.position = match.matchPosition;
      }
      return [
        ...acc,
        {
          ...item,
          children,
        },
      ];
    }
    return acc;
  }, []);
};

interface SidebarTreeViewProps {
  section: SidebarSection;
  searchResults: SearchResult[] | null;
  selectedItem: string | null;
  expandedItems: string[];
  setExpandedItems: (expandedItems: string[]) => void;
  onItemClick: () => void;
}

const SEGMENTER_SUPPORTED = Intl && Intl.Segmenter;

function Label(props) {
  const locale = useLocale();

  if (!SEGMENTER_SUPPORTED || !props.inFilterMode) {
    return (
      <Typography variant="subtitle2" component="div">
        {props.children}
      </Typography>
    );
  }

  let currentPosition = 0;
  const segmenter = new Intl.Segmenter(locale, { granularity: 'word' });
  const highlighted = Array.from(segmenter.segment(props.children)[Symbol.iterator](), ({ segment }, i) => {
    if (props.matchPosition == undefined) {
      return React.createElement(React.Fragment, { key: i }, segment);
    }

    const isInRange = currentPosition <= props.matchPosition && props.matchPosition <= currentPosition + segment.length;
    currentPosition += segment.length;
    const element = isInRange ? 'b' : React.Fragment;
    return React.createElement(element, { key: `${i}-${segment}` }, segment);
  });

  return (
    /* eslint-disable-next-line react/no-children-prop */
    <Typography variant="subtitle2" component="div" children={highlighted} />
  );
}

const TreeItem = function TreeItem(props) {
  const { inFilterMode, slots, ...rest } = props;
  const { publicAPI } = useTreeItem2Utils({
    itemId: props.itemId,
    children: props.children,
  });

  const item = publicAPI.getItem(props.itemId);

  return (
    <TreeItem2
      {...rest}
      slots={{
        ...slots,
        label: Label,
      }}
      slotProps={{
        label: { matchPosition: item?.position, inFilterMode },
      }}
    />
  );
};

export function SidebarTreeView(props: SidebarTreeViewProps) {
  const { section, searchResults, selectedItem, expandedItems, setExpandedItems, onItemClick } = props;
  const router = useRouter();
  const apiRef = useTreeViewApiRef();

  // If search results are not set, show all items
  // otherwise, filter out only the items that are in the search results
  const items = React.useMemo(
    () => (searchResults === null ? section.items : filterItems(section.items, searchResults)),
    [searchResults, section.items],
  );

  const handleItemExpansionToggle = (e: React.SyntheticEvent, itemId: string, isExpanded: boolean) => {
    const item = apiRef.current?.getItem(itemId);

    const isSidebarLink = !(e.target instanceof SVGElement) && item && item.href;
    if (isSidebarLink) {
      return;
    }

    if (isExpanded) {
      setExpandedItems([...expandedItems, itemId]);
    } else {
      setExpandedItems(expandedItems.filter((id) => id !== itemId));
    }
  };

  const handleClick = (e: React.MouseEvent<HTMLDivElement>, itemId: string) => {
    // Do not navigate when clicking on expand icon
    if (e.target instanceof SVGElement) return;

    const item = apiRef.current?.getItem(itemId);
    if (item && item.href) {
      onItemClick();
      router.push(item.href);
    }
  };

  // Disable transitions on the Collapse component
  // to avoid a flash effect when navigating between pages
  const CollapseComponent = (collapseProps) => <Collapse {...collapseProps} timeout={0}></Collapse>;

  return (
    <Stack spacing={2}>
      <Typography variant="h6" component="div">
        {section.title}
      </Typography>
      <Box>
        <RichTreeView
          items={items}
          onSelectedItemsChange={handleClick}
          selectedItems={selectedItem}
          expandedItems={expandedItems}
          onItemExpansionToggle={handleItemExpansionToggle}
          slots={{ item: TreeItem }}
          slotProps={{
            item: {
              inFilterMode: searchResults !== null,
              slots: { groupTransition: CollapseComponent },
            } as TreeItemProps & { inFilterMode: boolean },
          }}
          apiRef={apiRef}
          sx={{
            '.MuiTreeItem-root': {
              paddingTop: 0.5,
            },
            '.MuiTreeItem-groupTransition': {
              paddingLeft: 0.5,
            },
            '.MuiTreeItem-content': {
              paddingLeft: 0.5,
            },
            '.MuiTreeItem-label': {
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            },
          }}
        />
      </Box>
    </Stack>
  );
}
